import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

interface LogAction {
  datetime: string;
  description: string;
}

interface CancelTotals {
  requested: number;
  canceled: number;
  skipped: number;
  errors: number;
  ordersCanceled: number;
  ordersCopied: number;
  tuitionOrdersNotRecreated: number;
}

interface CancelInvoiceItem {
  invoiceId: number;
  invoiceNumber: number;
  customerName: string;
  outcome: 'CANCELED' | 'SKIPPED' | 'ERROR';
  reason: string;
  finances: 'NOT_APPLICABLE' | 'CANCELED' | 'FAILED';
  canceledOrderNumbers: number[];
  ordersCopied: number;
  tuitionOrdersNotRecreated: number;
}

interface CancelResultData {
  marinaId: number;
  userId: number;
  processControlId: number;
  invoiceIdsRequested: number[];
  totals: CancelTotals;
  invoices: CancelInvoiceItem[];
  log: LogAction[];
}

@Component({
  selector: 'app-invoice-cancel-result-dialog',
  templateUrl: './invoice-cancel-result-dialog.component.html',
  styleUrls: ['./invoice-cancel-result-dialog.component.scss'],
})
export class InvoiceCancelResultDialogComponent {

  data: CancelResultData;
  totals: CancelTotals = {
    requested: 0, canceled: 0, skipped: 0, errors: 0, ordersCanceled: 0, ordersCopied: 0,
    tuitionOrdersNotRecreated: 0
  };
  invoices: CancelInvoiceItem[] = [];
  logActions: LogAction[] = [];

  constructor(public dialogRef: DynamicDialogRef, public config: DynamicDialogConfig) {
    if (this.config.data) {
      this.data = this.config.data;
      this.totals = { ...this.totals, ...(this.data.totals || {}) };
      this.invoices = this.data.invoices || [];
      this.logActions = this.data.log || [];
    }
  }

  get showInvoicesAsTable(): boolean {
    return this.invoices.length > 0;
  }

  get hasTuitionOrders(): boolean {
    return (this.totals.tuitionOrdersNotRecreated || 0) > 0;
  }

  get tuitionNoticeText(): string {
    const qtd = this.totals.tuitionOrdersNotRecreated;
    return `${qtd} pedido(s) de mensalidade foram cancelados e NÃO são recriados: pedido de mensalidade nunca é `
      + 'copiado no cancelamento — a mensalidade correspondente é cancelada junto. É por isso que '
      + `"pedidos recriados" (${this.totals.ordersCopied}) é menor que "pedidos cancelados" `
      + `(${this.totals.ordersCanceled}).`;
  }

  recreatedDetail(item: CancelInvoiceItem): string {
    const tuition = item.tuitionOrdersNotRecreated || 0;
    return tuition > 0 ? `${tuition} mensalidade(s) não recriada(s)` : '';
  }

  outcomeLabel(item: CancelInvoiceItem): string {
    switch (item.outcome) {
      case 'CANCELED': return 'Cancelada';
      case 'SKIPPED': return 'Ignorada';
      case 'ERROR': return 'Erro';
      default: return item.outcome;
    }
  }

  outcomeClass(item: CancelInvoiceItem): string {
    switch (item.outcome) {
      case 'CANCELED': return 'status-success';
      case 'SKIPPED': return 'status-unknown';
      case 'ERROR': return 'status-error';
      default: return 'status-unknown';
    }
  }

  outcomeIcon(item: CancelInvoiceItem): string {
    switch (item.outcome) {
      case 'CANCELED': return 'pi pi-check';
      case 'SKIPPED': return 'pi pi-minus-circle';
      case 'ERROR': return 'pi pi-times';
      default: return 'pi pi-question-circle';
    }
  }

  financesLabel(item: CancelInvoiceItem): string {
    switch (item.finances) {
      case 'CANCELED': return 'Cancelado no financeiro';
      case 'FAILED': return 'Falha no financeiro';
      case 'NOT_APPLICABLE': return 'Sem programação';
      default: return item.finances;
    }
  }

  financesClass(item: CancelInvoiceItem): string {
    switch (item.finances) {
      case 'CANCELED': return 'status-success';
      case 'FAILED': return 'status-error';
      default: return 'status-unknown';
    }
  }

  ordersLabel(item: CancelInvoiceItem): string {
    const orders = item.canceledOrderNumbers || [];
    return orders.length > 0 ? orders.join(', ') : '—';
  }

  formatDateTime(dateTime: string): string {
    if (!dateTime) {
      return '';
    }
    return new Date(dateTime).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    });
  }

  getTimeDifference(index: number): string {
    if (index === 0) {
      return '0.00s';
    }
    const current = new Date(this.logActions[index].datetime);
    const previous = new Date(this.logActions[index - 1].datetime);
    return ((current.getTime() - previous.getTime()) / 1000).toFixed(2) + 's';
  }

  getActionIcon(description: string): string {
    if (description.includes('Início')) return 'pi pi-play-circle';
    if (description.includes('ERRO') || description.includes('Erro')) return 'pi pi-times-circle';
    if (description.includes('ATENÇÃO')) return 'pi pi-exclamation-triangle';
    if (description.includes('cancelada') || description.includes('finalizado')) return 'pi pi-check-circle';
    if (description.includes('impedida') || description.includes('ignorada')) return 'pi pi-minus-circle';
    if (description.includes('financeiro')) return 'pi pi-dollar';
    if (description.includes('Grupo') || description.includes('rateio')) return 'pi pi-sitemap';
    if (description.includes('pedido')) return 'pi pi-shopping-cart';
    if (description.includes('Processo iniciado')) return 'pi pi-cog';
    return 'pi pi-info-circle';
  }

  getActionClass(description: string): string {
    if (description.includes('ERRO') || description.includes('Erro')) return 'error-action';
    if (description.includes('ATENÇÃO')) return 'warning-action';
    if (description.includes('impedida') || description.includes('ignorada')) return 'warning-action';
    if (description.includes('cancelada') || description.includes('finalizado')) return 'success-action';
    return 'info-action';
  }
}
